import { isAfter, isBefore, isValid } from 'date-fns';
import { isUndefined } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import * as yup from 'yup';
import { SubmitHandler } from 'react-hook-form';
import { formatInTimeZone } from 'domains/dates/format';
import { useWatchCasted } from 'utils/hooks/useWatchCasted';

import { Form, FormField, SubmitButton, TextInput } from 'components/Forms';
import { Heading } from 'components/Typography';
import { ScreenerContext, withScreenerMachineState } from 'domains/signup/screener/context/screenerContext';
import { getPersonAge } from 'utils/date';
import { TherapyTarget } from 'domains/signup/data/types';
import { dateValidator } from 'utils/validators/dobField';

type EnterDobForm = {
  dob: Date;
};

const MIN_VALID_DATE = new Date(1900, 0, 0);

export const getFormSchema = (t: TFunction): yup.ObjectSchema<EnterDobForm> =>
  yup
    .object({
      dob: dateValidator
        .typeError(t('form.validation.fieldIsRequired'))
        .max(new Date(), t('form.validation.date.notInTheFuture'))
        .min(MIN_VALID_DATE, t('form.validation.date.tooSoon'))
        .required(t('form.validation.fieldIsRequired')),
    })
    .required();

type Props = {
  defaultValues?: Partial<EnterDobForm>;
  onSubmit: SubmitHandler<EnterDobForm>;
  target: TherapyTarget;
};

const EnterDob: React.FC<Props> = ({ onSubmit, target, defaultValues }) => {
  const { t } = useTranslation();

  return (
    <Form schema={getFormSchema(t)} flashErrors={false} onSubmit={onSubmit} defaultValues={defaultValues}>
      <DOBForm target={target} />
    </Form>
  );
};

const DOBForm: React.FC<Pick<Props, 'target'>> = ({ target }) => {
  const { t } = useTranslation();

  const value = useWatchCasted<Date>('dob');

  const formattedAge = useMemo(() => getPersonAge(t, value), [value, t]);

  return (
    <>
      <Heading level="h2" className="mb-16">
        {t(`screener.steps.enterDob.${target}.title`)}
      </Heading>
      <Heading level="h7" as="h3" className="mb-64 sm-d:mb-40">
        {t(`screener.steps.enterDob.${target}.subtitle`)}
      </Heading>

      <FormField
        component={TextInput}
        label={t(`screener.steps.enterDob.${target}.inputTitle`)}
        hint={
          value && isBefore(value, new Date()) && isAfter(value, MIN_VALID_DATE)
            ? t(`screener.steps.enterDob.${target}.ageHint`, { age: formattedAge })
            : undefined
        }
        name="dob"
        type="date"
        max={formatInTimeZone(new Date(), 'UTC', 'yyyy-MM-dd')}
      />

      <SubmitButton size="lg" disabled={isUndefined(value) || !isValid(value)} className="mt-64 sm-d:mt-40 sm-d:w-full">
        {t('screener.steps.enterDob.submit')}
      </SubmitButton>
    </>
  );
};

const EnterDobFormWrapper = () => {
  const target = ScreenerContext.useSelector(({ context }) => context.target);
  const dob = ScreenerContext.useSelector(({ context }) => context.dob);
  const { send } = ScreenerContext.useActorRef();

  return (
    <EnterDob
      onSubmit={data => {
        send({
          type: 'submitDob',
          dob: data.dob as Date,
        });
      }}
      defaultValues={{
        dob: dob,
      }}
      target={target as TherapyTarget}
    />
  );
};

export const ScreenerEnterDob = withScreenerMachineState(EnterDobFormWrapper, 'enterDob');
